import {
  ArrayInput,
  AutocompleteInput,
  maxLength,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import Edit from "@mui/icons-material/Edit";
import { ERASMUS_REJECTION_STATUSES, ErasmusApplicationStatuses } from "utils/constants";
import { Grid } from "@mui/material";
import FormModal from "server/components/Modals/FormModal";

import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import { settings } from "app";
import { useState } from "react";
import { erasmusRequests } from "api/erasmusApi";
import { administrationRequests } from "api/administrationApi";
import { useNotifyError } from "utils/useNotifyError";

const ChangeErasmusStatusesModal = ({ label = "Редакция на статус", record = null }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();

  const [nextStatuses, setNextStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const isHaveToAddInterviewData = selectedStatus === ErasmusApplicationStatuses.InterviewScheduled;
  const isHaveToAddUniversity = selectedStatus === ErasmusApplicationStatuses.InterviewApproved;
  const isMakeToAddFunding = selectedStatus === ErasmusApplicationStatuses.FinalApproved;
  const isRejection = ERASMUS_REJECTION_STATUSES.includes(selectedStatus);
  const handleStatusChange = value => setSelectedStatus(value);

  // loaded on open instead of through a ReferenceInput: the endpoint returns an empty list
  // whenever Ids[] is sent, and a ReferenceInput sends it as soon as the input holds a value
  const handleOpen = async () => {
    setSelectedStatus(null);

    try {
      const { data } = await dataProvider.customRequest(
        erasmusRequests.getApplicationsNextStatusSelect(record?.status),
        null,
        "GET",
      );

      if (!data?.length) {
        notify("Няма допустима промяна на статуса за това заявление", { type: "warning" });
        return false;
      }

      setNextStatuses(data);
    } catch (err) {
      notifyError(err);
      return false;
    }
  };

  const buildPayload = data => {
    if (isHaveToAddInterviewData) {
      return { status: data.status, interview: { start: data.start, end: data.end, notes: data?.notes } };
    }

    if (isHaveToAddUniversity) return { status: data.status, university: { id: data.universityId } };

    if (isMakeToAddFunding) return { status: data.status, fundingTranchesDto: data.fundingTranchesDto };

    if (isRejection && data.rejectionComment) {
      return { status: data.status, rejectionComment: data.rejectionComment };
    }

    return { status: data.status };
  };

  const handleSent = async data => {
    if (!selectedStatus) {
      notify("Изберете статус", { type: "error" });
      return false;
    }

    await dataProvider.customRequest(erasmusRequests.updateErasmusStatus(data.id), buildPayload(data), "PUT");
    refresh();
    notify(`Успешна редакция на статус`, { type: "success" });
  };

  const DatePart = ({ labelStart = "Начало на интервю", labelEnd = "Край на интервю" }) => {
    return (
      <>
        <Grid
          item
          xs={12}
          md={6}
        >
          <CustomDateTimeInput
            source="start"
            label={labelStart}
            validate={required()}
            required={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <CustomDateTimeInput
            source="end"
            label={labelEnd}
            validate={required()}
            required={true}
          />
        </Grid>
      </>
    );
  };
  return (
    <FormModal
      titleContent={"Редакция на статус"}
      onConfirm={handleSent}
      startIcon={<Edit />}
      titleButton={label}
      btnStyle={{ width: "max-content" }}
      variant="contained"
      defaultValues={null}
      onOpen={handleOpen}
    >
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{ mx: "auto" }}
        >
          <SelectInput
            source="status"
            label="Статус"
            choices={nextStatuses}
            optionText="nameBg"
            validate={required()}
            fullWidth
            onChange={e => handleStatusChange(e.target.value)}
          />
        </Grid>
        {isHaveToAddInterviewData ? (
          <>
            <DatePart />
            <Grid
              item
              xs={12}
            >
              <TextInput
                source="notes"
                label="Бележка"
                sx={settings.style.textAria}
                variant="outlined"
                fullWidth
                multiline
              />
            </Grid>
          </>
        ) : null}
        {isHaveToAddUniversity ? (
          <>
            <Grid
              item
              xs={12}
            >
              <ReferenceInput
                source="universityId"
                reference={administrationRequests.getUniversitiesSelect()}
              >
                <AutocompleteInput
                  label="Университет"
                  optionText={"name"}
                  validate={required()}
                  filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                />
              </ReferenceInput>
            </Grid>
          </>
        ) : null}
        {isMakeToAddFunding ? (
          <ArrayInput
            source="fundingTranchesDto"
            label="Финансиране"
            validate={required()}
          >
            <SimpleFormIterator
              sx={{
                "& .RaSimpleFormIterator-line": {
                  paddingY: 1,
                  paddingX: 2,
                  marginBottom: 3,
                },
              }}
            >
              <NumberInput
                source="totalAmount"
                label="Сума"
                validate={required()}
                onWheel={e => e.target.blur()}
              />
            </SimpleFormIterator>
          </ArrayInput>
        ) : null}
        {isRejection ? (
          <Grid
            item
            xs={12}
          >
            <TextInput
              source="rejectionComment"
              label="Причина за отказ"
              sx={settings.style.textAria}
              variant="outlined"
              fullWidth
              multiline
              validate={maxLength(2000)}
            />
          </Grid>
        ) : null}
      </Grid>
    </FormModal>
  );
};
export default ChangeErasmusStatusesModal;
